/**
 * Patient flow contracts beyond the status step (NEW-WF-06; research
 * sched-flow SF-05..SF-11, SF-19, WF-06). Routes under `/v1/schedule`:
 *
 * - `POST /appointments/:id/ready-for-doctor`   Ready for Dr from the card or panel (D key)
 * - `POST /appointments/:id/doctor-in`          automatic Doctor in (chart opened on the room's device)
 * - `POST /appointments/:id/flow-times`         manager-only "correct times" (past days too)
 * - `GET  /flow/notifications?since=`           this user's flow pings (shell listener)
 * - `GET  /flow/subscription`, `PUT /flow/subscription`   per-user providers x rooms x events
 * - `GET  /v1/analytics/reports/patient-flow?from=&to=`  Patient Flow report (median / p90 per provider)
 */
import { z } from 'zod';
import { DoctorReasonSchema } from './lightboard.js';

const Id = z.uuid();
const Instant = z.iso.datetime({ offset: true });
const LocalDate = z.iso.date();
const Key = z.string().min(8).max(200);

/** Who may correct flow times on past days (WF-06). */
export const FLOW_CORRECT_PERMISSION = 'settings.manage';

/* ------------------------------------------------------------------------ */
/* Ready for Dr / Doctor in                                                  */
/* ------------------------------------------------------------------------ */

export const ReadyForDoctorRequestSchema = z
  .object({
    idempotencyKey: Key,
    reason: DoctorReasonSchema.default('hyg_exam'),
    source: z.enum(['desk', 'chair']).default('desk'),
    /** A line from the hygienist added to the clinical summary ("wants to discuss SRP"). */
    note: z.string().trim().max(200).optional(),
  })
  .strict();
export type ReadyForDoctorRequest = z.output<typeof ReadyForDoctorRequestSchema>;
export type ReadyForDoctorRequestInput = z.input<typeof ReadyForDoctorRequestSchema>;

export const DoctorInRequestSchema = z
  .object({
    idempotencyKey: Key,
    /** The operatory the device sits in (workstation presence). */
    operatoryId: Id,
  })
  .strict();
export type DoctorInRequest = z.infer<typeof DoctorInRequestSchema>;

/** `POST /v1/schedule/flow/chart-opened`: a chart opened on a device in this room (SF-09). */
export const ChartOpenedRequestSchema = z
  .object({
    patientId: Id,
    operatoryId: Id,
  })
  .strict();
export type ChartOpenedRequest = z.infer<typeof ChartOpenedRequestSchema>;

export const FlowSignalResultSchema = z.object({
  /** The Light Board signal raised or cleared; null when nothing changed. */
  signalId: Id.nullable(),
  operatoryId: Id.nullable(),
  outcome: z.enum(['applied', 'duplicate', 'not_needed']),
  /**
   * Ready for Dr: the clinical summary sent with the request (deeper sites,
   * BOP, x-rays today, findings, planned perio therapy). Absent elsewhere.
   */
  summary: z.string().max(600).nullable().optional(),
});
export type FlowSignalResult = z.infer<typeof FlowSignalResultSchema>;

/* ------------------------------------------------------------------------ */
/* Correct times (manager)                                                   */
/* ------------------------------------------------------------------------ */

export const FlowTimesRequestSchema = z
  .object({
    idempotencyKey: Key,
    expectedVersion: z.number().int().nonnegative(),
    arrivedAt: Instant.nullable().optional(),
    seatedAt: Instant.nullable().optional(),
    dismissedAt: Instant.nullable().optional(),
    reason: z.string().trim().min(3).max(500),
  })
  .strict()
  .superRefine((r, ctx) => {
    const keys = ['arrivedAt', 'seatedAt', 'dismissedAt'] as const;
    if (keys.every((k) => r[k] === undefined)) {
      ctx.addIssue({ code: 'custom', message: 'send at least one time to correct' });
    }
  });
export type FlowTimesRequest = z.infer<typeof FlowTimesRequestSchema>;

/* ------------------------------------------------------------------------ */
/* Notifications                                                             */
/* ------------------------------------------------------------------------ */

export const FLOW_NOTIFY_EVENTS = [
  'here',
  'ready',
  'chair',
  'checkout',
  'needs_doctor',
  'escalation',
] as const;
export const FlowNotifyEventSchema = z.enum(FLOW_NOTIFY_EVENTS);
export type FlowNotifyEvent = z.infer<typeof FlowNotifyEventSchema>;

/** Default events of a user who never set a subscription. */
export const DEFAULT_FLOW_EVENTS: readonly FlowNotifyEvent[] = [
  'ready',
  'checkout',
  'needs_doctor',
];

/** SSE entity key for a new flow ping (ids only). */
export const FLOW_NOTIFICATION_ENTITY = 'flow_notification';

export const FlowNotificationSchema = z.object({
  id: Id,
  kind: FlowNotifyEventSchema,
  locationId: Id,
  appointmentId: Id.nullable(),
  operatoryId: Id.nullable(),
  operatoryName: z.string().nullable(),
  providerId: Id.nullable(),
  providerName: z.string().nullable(),
  /** "Jenna T." (first name + last initial), never the full name. */
  patientLabel: z.string().nullable(),
  actorUserId: Id.nullable(),
  actorName: z.string().nullable(),
  occurredAt: Instant,
  /** The toast's action button. */
  action: z.enum(['seat', 'checkout', 'on_my_way', 'open']),
  /** Whether this user's subscription asks for the chime. */
  chime: z.boolean(),
  /** The Light Board signal behind a needs_doctor / escalation ping. */
  signalId: Id.nullable(),
  /** Ready for Dr: the hygienist's clinical summary (chart.view only). Absent from older servers. */
  detail: z.string().nullable().optional(),
});
export type FlowNotification = z.infer<typeof FlowNotificationSchema>;

export const FlowNotificationsQuerySchema = z.object({
  since: Instant.optional(),
});
export const FlowNotificationListSchema = z.object({
  serverNow: Instant,
  items: z.array(FlowNotificationSchema),
});
export type FlowNotificationList = z.infer<typeof FlowNotificationListSchema>;

/**
 * "Next for me" (dentist role test, finding 2 / automation 3): what the
 * signed-in clinician should do next across their chairs. Exams ready
 * (open Ready for Dr for them or in their chairs), their seated visits
 * running past the booked length, and their notes to sign (the Notes
 * queue's own count). Each patient item opens the patient's chart.
 */
export const NEXT_FOR_ME_KINDS = ['exam_ready', 'chair_over', 'notes_to_sign'] as const;
export const NextForMeItemSchema = z.object({
  kind: z.enum(NEXT_FOR_ME_KINDS),
  /** Stable key for the list (signal, appointment or "notes"). */
  key: z.string(),
  locationId: Id.nullable(),
  patientId: Id.nullable(),
  /** "Jenna T."; null for the notes item. */
  patientLabel: z.string().nullable(),
  appointmentId: Id.nullable(),
  operatoryName: z.string().nullable(),
  /** Minutes waiting (exam) or over the booked length (chair). */
  minutes: z.number().int().nonnegative().nullable(),
  /** Notes to sign. */
  count: z.number().int().nonnegative().nullable(),
  /** Another clinician already said "On my way". */
  claimedBy: z.string().nullable(),
});
export type NextForMeItem = z.infer<typeof NextForMeItemSchema>;
export const NextForMeSchema = z.object({
  serverNow: Instant,
  /** The signed-in user's provider; null for staff (the list is then empty). */
  provider: z.object({ id: Id, name: z.string() }).nullable(),
  items: z.array(NextForMeItemSchema),
});
export type NextForMe = z.infer<typeof NextForMeSchema>;

export const FlowSubscriptionSchema = z.object({
  locationId: Id,
  /** Empty = every provider. */
  providerIds: z.array(Id).max(100),
  /** Empty = every room. */
  operatoryIds: z.array(Id).max(200),
  events: z.array(FlowNotifyEventSchema).max(FLOW_NOTIFY_EVENTS.length),
  chime: z.boolean(),
  mutedUntil: Instant.nullable(),
  /** False until the user saves one (defaults shown). */
  saved: z.boolean(),
});
export type FlowSubscription = z.infer<typeof FlowSubscriptionSchema>;

export const FlowSubscriptionPutSchema = FlowSubscriptionSchema.omit({ saved: true }).strict();
export type FlowSubscriptionPut = z.infer<typeof FlowSubscriptionPutSchema>;

/* ------------------------------------------------------------------------ */
/* Patient Flow report (SF-19)                                               */
/* ------------------------------------------------------------------------ */

export const FlowReportQuerySchema = z.object({
  from: LocalDate,
  to: LocalDate,
});

const Stat = z.object({
  /** Visits with this interval measured. */
  n: z.number().int().nonnegative(),
  medianMin: z.number().nullable(),
  p90Min: z.number().nullable(),
});
export type FlowStat = z.infer<typeof Stat>;

export const FlowReportRowSchema = z.object({
  providerId: Id.nullable(),
  providerName: z.string(),
  visits: z.number().int().nonnegative(),
  /** Check-in (or the start, when early) to seated. */
  wait: Stat,
  /** Ready for Dr raised to the doctor's claim / Doctor in. */
  doctorWait: Stat,
  /** Seated to dismissed. */
  chair: Stat,
  /** Dismissed to complete (checkout finished). */
  checkout: Stat,
  leftWithoutBeingSeen: z.number().int().nonnegative(),
});
export type FlowReportRow = z.infer<typeof FlowReportRowSchema>;

export const FlowReportSchema = z.object({
  from: LocalDate,
  to: LocalDate,
  rows: z.array(FlowReportRowSchema),
  total: FlowReportRowSchema,
  /** Step backs and manager corrections excluded from the numbers. */
  correctedEvents: z.number().int().nonnegative(),
});
export type FlowReport = z.infer<typeof FlowReportSchema>;
